import * as React from "react";
import { Grid, AlertColor } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import {
    useGetPayRatesQuery,
    useTogglePayRateActiveStatusMutation,
} from "../../../services/cclTokenedPayrollApi";
import { PayRate } from "../../../services/types/payrollEventTypes";
import AddEditFeeDrawer from "./addEditFeeDrawer";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

function getDateFromTimestamp(date: any) {
    if (!date) {
        return "";
    }
    return new Date(date).toLocaleDateString();
}

interface PayRatesPanelProps {}

const PayRatesPanel: React.FC<PayRatesPanelProps> = () => {
    const { data: payRatesData, isLoading: loading } = useGetPayRatesQuery();
    const payRatesResponse = (payRatesData ?? []) as
        | PayRate[]
        | { payRates?: PayRate[]; data?: PayRate[] };
    const [togglePayRateActiveStatus] = useTogglePayRateActiveStatusMutation();
    const { logEvent } = useLogAccessEvent();
    const [feeDrawerOpen, setFeeDrawerOpen] = React.useState<boolean>(false);
    const [drawerMode, setDrawerMode] = React.useState<"add" | "edit">("add");
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [selectedFee, setSelectedFee] = React.useState<PayRate | null>(null);

    // Snackbar state
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const rows = React.useMemo(() => {
        if (Array.isArray(payRatesResponse)) {
            return payRatesResponse;
        }

        if (Array.isArray(payRatesResponse.payRates)) {
            return payRatesResponse.payRates;
        }

        if (Array.isArray(payRatesResponse.data)) {
            return payRatesResponse.data;
        }

        return [];
    }, [payRatesResponse]);

    const activeField = React.useMemo(() => {
        if (!rows.length) {
            return null;
        }

        return (
            Object.keys(rows[0]).find((field) => field.toLowerCase() === "active") ?? null
        );
    }, [rows]);

    const normalizedRows = React.useMemo(() => {
        const normalized = activeField ? rows.map((row: any) => {
            const rawValue = row[activeField];
            if (typeof rawValue === "number") {
                return {
                    ...row,
                    [activeField]: rawValue === 1,
                };
            }
            if (typeof rawValue === "string") {
                const normalized = rawValue.trim().toLowerCase();
                if (normalized === "true" || normalized === "false") {
                    return {
                        ...row,
                        [activeField]: normalized === "true",
                    };
                }
                if (normalized === "1" || normalized === "0") {
                    return {
                        ...row,
                        [activeField]: normalized === "1",
                    };
                }
            }
            return row;
        }) : rows;

        return [...normalized].sort((a: any, b: any) =>
            String(a.rateCode ?? "").localeCompare(String(b.rateCode ?? ""))
        );
    }, [rows, activeField]);

    const getFeeRowId = React.useCallback((row: any) => {
        return row.id ?? row.payRateId ?? row.rateCode ?? JSON.stringify(row);
    }, []);

    const isExactlyOneSelected = selectedRows.length === 1;

    const currentSelectedFee = React.useMemo(() => {
        if (!isExactlyOneSelected) {
            return null;
        }
        const selectedId = String(selectedRows[0] ?? "").trim();
        return normalizedRows.find((row: any) => String(getFeeRowId(row)) === selectedId) ?? null;
    }, [isExactlyOneSelected, selectedRows, normalizedRows, getFeeRowId]);

    const deleteButtonCaption = React.useMemo(() => {
        return currentSelectedFee && currentSelectedFee.active ? "Disable Pay Rate" : "Enable Pay Rate";
    }, [currentSelectedFee]);

    const normalizeSelectionIds = (selection: any): Array<string | number> => {
        if (Array.isArray(selection)) {
            return selection as Array<string | number>;
        }

        if (selection?.ids instanceof Set) {
            return Array.from(selection.ids) as Array<string | number>;
        }

        if (Array.isArray(selection?.ids)) {
            return selection.ids as Array<string | number>;
        }

        return [];
    };

    const handleToolbarButtonClick = (btnId: string) => {
        if (btnId === "add-fee-button") {
            setDrawerMode("add");
            setSelectedFee(null);
            setFeeDrawerOpen(true);
            return;
        }

        if (btnId === "edit-fee-button") {
            if (!isExactlyOneSelected) {
                return;
            }

            const selectedId = String(selectedRows[0] ?? "").trim();
            const fee = normalizedRows.find((row: any) => String(getFeeRowId(row)) === selectedId);

            if (!fee) {
                return;
            }

            setDrawerMode("edit");
            setSelectedFee(fee);
            setFeeDrawerOpen(true);
            return;
        }

        if (btnId === "delete-fee-button") {
            if (!isExactlyOneSelected || !currentSelectedFee) {
                return;
            }

            const resolvedRateCode =
                currentSelectedFee.rateCode ?? currentSelectedFee.RateCode ?? currentSelectedFee.rate_code ?? currentSelectedFee.ratecode;

            togglePayRateActiveStatus({
                rateCode: String(resolvedRateCode ?? ""),
                rateDesc: String(currentSelectedFee.rateDesc ?? ""),
                unit: String(currentSelectedFee.unit ?? ""),
                currentFee: Number(currentSelectedFee.currentFee ?? 0),
                ultiproText: String(currentSelectedFee.ultiproText ?? ""),
                active: !currentSelectedFee.active,
            })
                .unwrap()
                .then(() => {
                    logEvent(!currentSelectedFee.active ? "FeeEnabled" : "FeeDisabled", null);
                    setSelectedRows([]);

                    // Show success snackbar
                    const message = !currentSelectedFee.active ? "Pay Rate Enabled" : "Pay Rate Disabled ";
                    setSnackbarMessage(message);
                    setSnackbarSeverity("success");
                    setShowSnackbar(true);
                })
                .catch((error) => {
                    console.error("Error toggling fee:", error);
                    
                    // Show error snackbar
                    setSnackbarMessage("Failed to toggle Fee");
                    setSnackbarSeverity("error");
                    setShowSnackbar(true);
                });
        }
    };

    const columns = [
        {
            field: "rateCode",
            headerName: "Code",
            width: 200,
        },
        {
            field: "rateDesc",
            headerName: "Description",
            width: 200,
        },
        {
            field: "active",
            headerName: "Enabled",
            type: "boolean",
            renderCell: (params: any) => {
                if (params.value === true) {
                    return "Yes";
                } else {
                    return "No";
                }
            },
            width: 100,
        },
        {
            field: "unit",
            headerName: "Unit",
            width: 100,
        },
        {
            field: "currentFee",
            headerName: "Current Fee",
            width: 100,
        },
        {
            field: "ultiproText",
            headerName: "Ultipro Text",
            width: 200,
        },
                {
            field: "createdBy",
            headerName: "Created By",
            width: 100,
        },
        {
            field: "created",
            headerName: "Created",
            valueGetter: (params: any) => getDateFromTimestamp(params.row.created),
            width: 100,
        },
        {
            field: "modifiedBy",
            headerName: "Modified By",
            width: 100,
        },
        {
            field: "modified",
            headerName: "Modified",
            valueGetter: (params: any) => getDateFromTimestamp(params.row.modified),
            width: 100,
        }
    ];

    return (
        <Grid
            container
            spacing={0}
            alignItems="stretch"
            sx={{ justify: "space-between", flexGrow: 1 }}
        >
            <AddEditFeeDrawer
                open={feeDrawerOpen}
                mode={drawerMode}
                fee={selectedFee}
                onClose={() => {
                    setFeeDrawerOpen(false);
                    setSelectedFee(null);
                }}
                onSaveResult={(message, severity) => {
                    setSnackbarMessage(message);
                    setSnackbarSeverity(severity);
                    setShowSnackbar(true);
                }}
            />
            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />
            <Grid
                item
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: 1,
                    height: "calc(100vh - 220px)",
                    minHeight: 400,
                }}
            >
                <DataGridPremium
                    columns={columns}
                    components={{ Toolbar: CclDataGridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showSaveState: false,
                            hideSettingsButtons: true,
                            savestate: null,
                            clearstate: null,
                            anySelected: isExactlyOneSelected,
                            miscButtons: [
                                {
                                    id: "add-fee-button",
                                    caption: "Add Pay Rate",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                },
                                {
                                    id: "edit-fee-button",
                                    caption: "Edit Pay Rate",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                    disable: !isExactlyOneSelected,
                                },
                                {
                                    id: "delete-fee-button",
                                    caption: deleteButtonCaption,
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                    disable: !isExactlyOneSelected,
                                },
                            ],
                            btnClick: handleToolbarButtonClick,
                        },
                    }}
                    getRowId={getFeeRowId}
                    rows={normalizedRows}
                    loading={loading}
                    disableSelectionOnClick
                    checkboxSelection
                    onSelectionModelChange={(newSelection: any) => {
                        setSelectedRows(normalizeSelectionIds(newSelection));
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default PayRatesPanel;
